import { Icon } from "@captions-web/icons";
import { makeBrandedId } from "@captions-web/utils/brand";
import { clamp } from "@captions-web/utils/clamp";
import { t } from "i18next";
import { nanoid } from "nanoid";
import { useCallback, useMemo } from "react";
import { useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { toast } from "sonner";
import { useThrottledCallback } from "use-debounce";

import { Button, PlayButton } from "~/components/primitives/button";
import { Spinner } from "~/components/primitives/spinner";
import { formatTimeMs } from "~/lib/format-time";
import { useLatestRef, useOnChangeEffect, useOnUnmountEffect } from "~/lib/hooks/react-helpers";
import { useDragOnPointerDown } from "~/lib/hooks/use-drag-on-pointer-down";
import { HOTKEYS } from "~/lib/hotkeys";
import { css, cva, cx } from "~/styled-system/css";

import { AudioProcessor } from "../../audio-processor";
import { END_CLAMP_MAX } from "../../constants";
import { ensureRegionIncrementSize } from "../../ensure-region-increment-size";
import { urlToAudioFile } from "../../url-to-audio-file";
import { audioSamplesToSvg } from "../../waveform-svg-renderer";

import { AudioTrimmerRegion } from "./audio-trimmer-region";

export const TrimmableRegion_Id = makeBrandedId("tar");
export type TrimmableRegion_Id = ReturnType<typeof TrimmableRegion_Id>;

export const WAVEFORM_HEIGHT = "50px";
export const PLAYHEAD_HEIGHT = "68px";
export const WAVEFORM_OUTER_HEIGHT = "100px";
const TOP_FLOATER_INDICATOR_WIDTH = 20;
const POINTER_EVENTS_THROTTLE_MS = 50;
export const FRAME_INCREMENT_SIZE_25_FPS = 40; // 40ms per frame for 25fps

export interface TrimmableRegion {
  id: TrimmableRegion_Id;
  startMs: number;
  endMs: number;
  index: number;
}

export interface AudioTrimmerProps {
  audio?: File | string;
  trimMode?: "regions" | "duration" | "none";
  audioProcessor?: AudioProcessor;
  // can be used by external caller for loading indicator
  isPending?: boolean;

  minRegionSizeMs?: number;
  maxRegionSizeMs?: number;
  initialRegions: TrimmableRegion[];
  // fired as the user is dragging the regions
  onRegionsChange?: (regions: TrimmableRegion[]) => void;
  // fired when the user is done dragging the regions
  onCommitRegions?: () => void;
  playButtonVariant?: "inline" | "bottom";
  hideTimestamps?: boolean;
  loopDurationRegion?: boolean;
  bottom?: React.ReactNode;
  onTimeUpdate?: (t: number) => void;
  onDurationChange?: (duration: number) => void;
  disabled?: boolean;
  className?: string;
  defaultTip?: string;
  minErrorTip?: string;
  maxErrorTip?: string;
}

/* eslint-disable jsx-a11y/click-events-have-key-events */

export function AudioTrimmer({
  audio,
  minRegionSizeMs = 500,
  maxRegionSizeMs = Infinity,
  onRegionsChange,
  onCommitRegions,
  playButtonVariant = "bottom",
  trimMode = "duration",
  hideTimestamps = false,
  initialRegions,
  loopDurationRegion,
  bottom,
  onTimeUpdate,
  onDurationChange,
  audioProcessor: audioProcessorProp,
  isPending = false,
  disabled,
  className,
  minErrorTip,
  maxErrorTip,
  defaultTip,
}: AudioTrimmerProps) {
  const [svg, setSvg] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTimeMs, setCurrentTimeMs] = useState(0);
  const audioProcessor = useRef<AudioProcessor>();
  const [audioDurationMs, setAudioDurationMs] = useState<number>(0);
  const [regions, setRegions] = useState<TrimmableRegion[]>(
    ensureRegionIncrementSize(initialRegions)
  );
  const scrubbingRef = useRef(false);
  const wasPlayingAtBeginningOfDrag = useRef(false);
  const waveFormContainerRef = useRef<HTMLDivElement>(null);
  const [isLoadingAudioFile, setIsLoadingAudioFile] = useState(typeof audio === "string");
  const [selectedRegionId, setSelectedRegionId] = useState<TrimmableRegion_Id | null>(null);
  const [isWaveformContainerHovered, setIsWaveformContainerHovered] = useState(false);
  const [waveformContainerPointerPosition, setWaveformContainerPointerPosition] = useState({
    x: 0,
    y: 0,
    progress: 0,
    timeMs: 0,
  });
  const playButtonRef = useRef<HTMLButtonElement>(null);
  const [canAddRegion, setCanAddRegion] = useState(false);
  const [errorTipType, setErrorTipType] = useState<
    "none" | "prevMin" | "prevMax" | "currentMin" | "currentMax"
  >("none");
  const latestOnCommitRegions = useLatestRef(onCommitRegions);
  const latestRegions = useLatestRef(regions);
  const latestSelectedRegionId = useLatestRef(selectedRegionId);
  const isLoading = isLoadingAudioFile || isPending;

  useOnChangeEffect(initialRegions, (newRegions) => {
    if (newRegions.length > 0 && regions.length < 1) {
      setRegions(ensureRegionIncrementSize(newRegions));
    }
  });

  const jumpToProgress = (progress: number) => {
    let time = progress * (audioProcessor.current?.durationMs ?? 0);
    // if it's duration mode, we don't want to allow seeking outside of duration region
    if (trimMode === "duration" && regions[0]) {
      time = clamp(time, regions[0].startMs, regions[0].endMs);
    }
    audioProcessor.current?.seek(time);
  };

  const throttledJumpToProgress = useThrottledCallback(jumpToProgress, POINTER_EVENTS_THROTTLE_MS);

  useOnChangeEffect(audio, () => {
    const processAudioFile = async () => {
      if (!audio) {
        return;
      }
      if (audioProcessor.current) {
        audioProcessor.current.destroy();
      }
      let file: File;
      // load the audio to a file if it's a url
      if (typeof audio === "string") {
        try {
          setIsLoadingAudioFile(true);
          file = await urlToAudioFile(audio);
        } catch (error) {
          console.error("Failed to load audio from URL:", error);
          toast.error(t("mirage-studio:audio-trimmer.failed-to-load-file"));
          return;
        } finally {
          setIsLoadingAudioFile(false);
        }
      } else {
        file = audio;
      }
      const processor = audioProcessorProp ?? new AudioProcessor();
      processor.on("error", () =>
        toast.error(t("mirage-studio:audio-trimmer.failed-to-load-file"))
      );
      await processor.loadAudio(file);
      processor.on("timeupdate", (event) => {
        setCurrentTimeMs(event?.currentTimeMs ?? 0);
        onTimeUpdate?.(event?.currentTimeMs ?? 0);
        setIsPlaying(event?.isPlaying ?? false);
      });
      processor.on("play", () => setIsPlaying(true));
      processor.on("pause", () => setIsPlaying(false));
      processor.on("ended", () => setIsPlaying(false));

      const nextDuration = processor.durationMs;
      setAudioDurationMs(nextDuration);
      onDurationChange?.(nextDuration);
      if (processor.downsampledData) {
        setSvg(audioSamplesToSvg(processor.downsampledData, "100%", WAVEFORM_HEIGHT));
      }
      audioProcessor.current = processor;
    };
    // kick off the audio processor
    processAudioFile();
  });

  const handleTogglePlay = async () => {
    const nextIsPlaying = !audioProcessor.current?.isPlaying;
    setIsPlaying(nextIsPlaying);

    if (nextIsPlaying) {
      audioProcessor.current?.play();
    } else {
      audioProcessor.current?.pause();
    }
  };

  const onWaveformDraggingChange = useCallback((dragging: boolean) => {
    if (dragging) {
      wasPlayingAtBeginningOfDrag.current = audioProcessor.current?.isPlaying || false;
      audioProcessor.current?.pause();
    } else {
      if (wasPlayingAtBeginningOfDrag.current) {
        audioProcessor.current?.play();
      }
      wasPlayingAtBeginningOfDrag.current = false;
    }
  }, []);

  const handlePointerMoveOverWaveform = (event: PointerEvent) => {
    const { clientX, clientY } = event;
    const parentRect = waveFormContainerRef.current?.getBoundingClientRect();
    if (!parentRect) {
      setIsWaveformContainerHovered(false);
      return;
    }
    const offsetWidth = waveFormContainerRef.current?.offsetWidth ?? 0;
    const x = clamp(clientX - parentRect.left, 0, offsetWidth);
    const y = clamp(clientY - parentRect.top, 0, waveFormContainerRef.current?.offsetHeight ?? 0);
    const progress = clamp(x / offsetWidth, 0, 100);
    const timeMs = progress * (audioProcessor.current?.durationMs ?? 0);

    setWaveformContainerPointerPosition({ x, y, progress, timeMs });
    // check if current pointer position is at a position that can create a new cut
    let isPositionValid = false;
    const currentRegion = regions.find(
      (region) => region.startMs < timeMs && region.endMs > timeMs
    );
    if (currentRegion) {
      const leftSpace = timeMs - currentRegion.startMs;
      const rightSpace = currentRegion.endMs - timeMs;
      isPositionValid = leftSpace >= minRegionSizeMs && rightSpace >= minRegionSizeMs;
    }

    setCanAddRegion(isPositionValid);
  };

  const throttledHandlePointerMove = useThrottledCallback(
    handlePointerMoveOverWaveform,
    POINTER_EVENTS_THROTTLE_MS
  );

  const handlePointerEnterWaveformContainer = () => {
    if (isLoading) {
      return;
    }
    setIsWaveformContainerHovered(true);
    document.addEventListener("pointermove", throttledHandlePointerMove);
  };

  const handlePointerLeaveWaveformContainer = () => {
    setIsWaveformContainerHovered(false);
    document.removeEventListener("pointermove", throttledHandlePointerMove);
  };

  const updateRegions = useCallback(
    (regions: TrimmableRegion[]) => {
      const nextRegions = ensureRegionIncrementSize(regions);
      setRegions(nextRegions);
      if (audioProcessor.current && nextRegions.length > 0 && loopDurationRegion) {
        const loopRegion = nextRegions[0];
        audioProcessor.current.setLoopRegion(loopRegion.startMs, loopRegion.endMs);
      }
      latestRegions.current = nextRegions;
      onRegionsChange?.(nextRegions);
    },
    // eslint-disable-next-line
    [loopDurationRegion, trimMode, onRegionsChange]
  );

  const onWaveformPointerdown = useCallback(
    (downEvent: React.PointerEvent<HTMLDivElement> | PointerEvent, prepareToDrag: boolean) => {
      const isMainWaveformClick = downEvent.currentTarget === waveFormContainerRef.current;
      // ignore right clicks
      if (isMainWaveformClick && downEvent.button !== 0) {
        return;
      }

      if (disabled) {
        return;
      }

      const startingClientX =
        "nativeEvent" in downEvent ? downEvent.nativeEvent.clientX : downEvent.clientX;
      // calculate offsetX relative to the container regardless of which element was clicked
      const containerRect = waveFormContainerRef.current?.getBoundingClientRect();
      const startingOffsetX = containerRect ? startingClientX - containerRect.left : 0;
      const offsetWidth = waveFormContainerRef.current?.offsetWidth ?? 0;
      throttledJumpToProgress(startingOffsetX / offsetWidth);

      if (!prepareToDrag) {
        return;
      }

      scrubbingRef.current = true;
      onWaveformDraggingChange(true);

      const onPointerMove = (moveEvent: PointerEvent) => {
        const containerRect = waveFormContainerRef.current?.getBoundingClientRect();
        const currentOffsetX = containerRect ? moveEvent.clientX - containerRect.left : 0;
        throttledJumpToProgress(clamp(currentOffsetX / offsetWidth, 0, END_CLAMP_MAX));
      };

      document.addEventListener("pointermove", onPointerMove);

      // we use this so if you stop dragging outside of the element
      // we still stop the scrubbing
      document.addEventListener(
        "pointerup",
        () => {
          scrubbingRef.current = false;
          onWaveformDraggingChange(false);
          document.removeEventListener("pointermove", onPointerMove);
        },
        { once: true }
      );
    },
    [disabled, onWaveformDraggingChange, throttledJumpToProgress]
  );

  const handlePlayheadDrag = useDragOnPointerDown({
    onStart: (_e) => {
      return {
        shouldStart: true,
        context: {},
      };
    },
    onMove: ({ event }) => {
      if (disabled) {
        return;
      }
      onWaveformPointerdown(event as PointerEvent, false);
    },
    onDone: () => {
      // noop
    },
  });

  const handleRegionResize = useCallback(
    (
      regionId: TrimmableRegion_Id,
      delta: { x: number; y: number },
      side: "left" | "right" | "center"
    ) => {
      const regionIndex = regions.findIndex((region) => region.id === regionId);
      if (regionIndex < 0) {
        console.error(`cannot find trimmable region with id: ${regionId}`);
        return;
      }
      const region = regions[regionIndex];
      const pixelWidth = waveFormContainerRef.current?.offsetWidth || 1;
      const msPerPixel = audioDurationMs / pixelWidth;
      if (trimMode === "duration") {
        if (side === "left") {
          let nextStart = region.startMs + delta.x * msPerPixel;
          const min = Math.max(0, region.endMs - maxRegionSizeMs);
          const max = region.endMs - minRegionSizeMs;

          if (nextStart < min) {
            setErrorTipType("currentMax");
          } else if (nextStart > max) {
            setErrorTipType("currentMin");
          } else {
            setErrorTipType("none");
          }

          nextStart = clamp(nextStart, min, max);
          const nextRegions = regions.map((r, i) =>
            i === regionIndex ? { ...r, startMs: nextStart } : r
          );
          updateRegions(nextRegions);
          const nextStartMs = latestRegions.current?.[0]?.startMs;
          if (nextStartMs != null && side === "left") {
            audioProcessor.current?.seek(nextStartMs);
          }
          return;
        }
        if (side === "right") {
          let nextEnd = region.endMs + delta.x * msPerPixel;
          const max = Math.min(audioDurationMs, region.startMs + maxRegionSizeMs);
          const min = region.startMs + minRegionSizeMs;

          if (nextEnd > max) {
            setErrorTipType("currentMax");
          } else if (nextEnd < min) {
            setErrorTipType("currentMin");
          } else {
            setErrorTipType("none");
          }

          nextEnd = clamp(nextEnd, min, max);
          const nextRegions = regions.map((r, i) =>
            i === regionIndex ? { ...r, endMs: nextEnd } : r
          );

          updateRegions(nextRegions);
          return;
        }
        if (side === "center") {
          const regionDuration = region.endMs - region.startMs;

          let nextStart = region.startMs + delta.x * msPerPixel;
          let nextEnd = nextStart + regionDuration;

          if (nextStart < 0) {
            nextStart = 0;
            nextEnd = regionDuration;
          }

          if (nextEnd > audioDurationMs) {
            nextEnd = audioDurationMs;
            nextStart = nextEnd - regionDuration;
          }

          const nextRegions = regions.map((r, i) =>
            i === regionIndex ? { ...r, startMs: nextStart, endMs: nextEnd } : r
          );

          updateRegions(nextRegions);
          return;
        }
      }
      if (trimMode === "regions") {
        // always passing in the left side for region trimming, right drag handle is always disabled
        if (side === "left") {
          const prevRegion = regions[regionIndex - 1];
          const nextStart = region.startMs + delta.x * msPerPixel;

          // Calculate individual constraints
          const currentRegionMaxConstraint = region.endMs - maxRegionSizeMs;
          const currentRegionMinConstraint = region.endMs - minRegionSizeMs;
          const prevRegionMinConstraint = prevRegion ? prevRegion.startMs + minRegionSizeMs : 0;
          const prevRegionMaxConstraint = prevRegion
            ? prevRegion.startMs + maxRegionSizeMs
            : audioDurationMs;

          // clamp based on both regions min and max size constraints
          const lowerBound = Math.max(0, currentRegionMaxConstraint, prevRegionMinConstraint);
          const upperBound = Math.min(currentRegionMinConstraint, prevRegionMaxConstraint);

          const clampedNextStart = clamp(nextStart, lowerBound, upperBound);

          if (nextStart !== clampedNextStart) {
            // Determine if it's a min or max constraint that's being hit
            if (nextStart < lowerBound) {
              // Determine which constraint is being hit for the lower bound
              if (lowerBound === prevRegionMinConstraint && prevRegion) {
                setErrorTipType("prevMin");
              } else if (lowerBound === currentRegionMaxConstraint) {
                setErrorTipType("currentMax");
              }
            } else if (nextStart > upperBound) {
              // Determine which constraint is being hit for the upper bound
              if (upperBound === currentRegionMinConstraint) {
                setErrorTipType("currentMin");
              } else if (upperBound === prevRegionMaxConstraint && prevRegion) {
                setErrorTipType("prevMax");
              }
            }
          } else {
            setErrorTipType("none");
          }
          const nextRegions = [...regions];
          nextRegions[regionIndex] = { ...region, startMs: clampedNextStart };
          if (prevRegion) {
            nextRegions[regionIndex - 1] = { ...prevRegion, endMs: clampedNextStart };
          }
          updateRegions(nextRegions);
          return;
        }
        if (side === "center") {
          return;
        }
        if (side === "right") {
          return;
        }
      }
    },
    // eslint-disable-next-line
    [minRegionSizeMs, trimMode, regions, audioDurationMs]
  );

  const handleSelectRegion = useCallback(
    (id: TrimmableRegion_Id) => {
      const region = latestRegions.current.find((item) => item.id === id);
      if (!region) {
        return;
      }
      if (trimMode === "regions") {
        audioProcessor.current?.seek(region.startMs);
      }
      setSelectedRegionId(id);
    },
    // eslint-disable-next-line
    [trimMode]
  );

  const handleCommitRegion = useCallback(() => {
    setErrorTipType("none");
    latestOnCommitRegions.current?.();
    const currentRegion = latestRegions.current.find(
      (item) => item.id === latestSelectedRegionId.current
    );
    if (currentRegion && trimMode === "regions") {
      audioProcessor.current?.seek(currentRegion.startMs);
    }
    // eslint-disable-next-line
  }, [trimMode]);

  const canDelete = useMemo(() => {
    if (!selectedRegionId) {
      return false;
    }

    const selectedIndex = regions.findIndex((region) => region.id === selectedRegionId);
    if (selectedIndex === -1) {
      return false;
    }

    if (regions.length <= 1) {
      return false;
    }

    // check if merging the regions to the left and right would exceed maxRegionSizeMs
    const leftRegion = regions[selectedIndex - 1];
    const rightRegion = regions[selectedIndex];
    if (!leftRegion) {
      return false;
    }
    const mergedDuration = rightRegion.endMs - leftRegion.startMs;
    return mergedDuration <= maxRegionSizeMs;
  }, [selectedRegionId, maxRegionSizeMs, regions]);

  const handleDelete = () => {
    if (!canDelete || !selectedRegionId) {
      return;
    }

    const selectedIndex = regions.findIndex((region) => region.id === selectedRegionId);

    const leftRegion = regions[selectedIndex - 1];
    const rightRegion = regions[selectedIndex];

    const updatedRegions = regions.reduce((memo, region, index) => {
      if (index === selectedIndex) {
        // skip the selected region (it will be merged in next step)
        return memo;
      }
      if (index === selectedIndex - 1) {
        // replace the left region with the merged one
        return [
          ...memo,
          {
            ...leftRegion,
            endMs: rightRegion.endMs,
            index: memo.length,
          },
        ];
      }
      return [
        ...memo,
        {
          ...region,
          index: memo.length,
        },
      ];
    }, [] as TrimmableRegion[]);

    updateRegions(updatedRegions);
    setSelectedRegionId(null);
    handleCommitRegion();
  };

  const handleAdd = (_e: React.MouseEvent) => {
    if (!canAddRegion) {
      return;
    }
    const { timeMs } = waveformContainerPointerPosition;
    let newRegionId: TrimmableRegion_Id | undefined = undefined;
    const nextRegions = regions.reduce((memo, current) => {
      if (current.startMs < timeMs && current.endMs > timeMs) {
        newRegionId = TrimmableRegion_Id(nanoid());
        const leftRegion = {
          ...current,
          endMs: timeMs,
          index: memo.length,
        };
        const rightRegion = {
          ...current,
          id: newRegionId,
          startMs: timeMs,
          index: memo.length + 1,
        };
        return [...memo, leftRegion, rightRegion];
      }
      // for regions that aren't being split, just update their index
      return [...memo, { ...current, index: memo.length }];
    }, [] as TrimmableRegion[]);

    updateRegions(nextRegions);
    if (newRegionId) {
      setSelectedRegionId(newRegionId);
    }
    audioProcessor.current?.seek(timeMs);
    handleCommitRegion();
  };

  const handleStartMoving = (_side: "left" | "right" | "center") => {
    wasPlayingAtBeginningOfDrag.current = audioProcessor.current?.isPlaying ?? false;
    if (trimMode === "duration" && wasPlayingAtBeginningOfDrag.current) {
      audioProcessor.current?.pause();
    }
  };

  const handleDoneMoving = (_side: "left" | "right" | "center") => {
    if (trimMode === "duration" && wasPlayingAtBeginningOfDrag.current) {
      audioProcessor.current?.play();
    }
    wasPlayingAtBeginningOfDrag.current = false;
  };

  const handleClickTimestamp = () => {
    audioProcessor.current?.seek(regions[0]?.startMs ?? 0);
  };

  useOnUnmountEffect(() => {
    audioProcessor.current?.off("play");
    audioProcessor.current?.off("pause");
    audioProcessor.current?.off("timeupdate");
    audioProcessor.current?.destroy();
  });

  useHotkeys(HOTKEYS.SPACE, (_e) => {
    // don't toggle play if the playbutton is focused
    if (document.activeElement !== playButtonRef.current) {
      handleTogglePlay();
    }
  });
  useHotkeys(HOTKEYS.LEFT, () => {
    const newTimeMs = Math.max(0, currentTimeMs - 1000); // move backward by 1 second
    audioProcessor.current?.seek(newTimeMs);
  });
  useHotkeys(HOTKEYS.RIGHT, () => {
    const newTimeMs = Math.min(audioDurationMs, currentTimeMs + 1000); // move forward by 1 seconds
    audioProcessor.current?.seek(newTimeMs);
  });

  const topFloaterPosition = {
    transform: `translate(${waveformContainerPointerPosition.x}px, -30px)`,
    opacity: isWaveformContainerHovered ? 1 : 0,
  };
  const topFloaterTimelineIndicatorPosition = {
    transform: `translate(${
      waveformContainerPointerPosition.x - 1 - TOP_FLOATER_INDICATOR_WIDTH / 2
    }px, 0px)`,
    opacity: isWaveformContainerHovered ? 1 : 0,
  };

  const playheadPosition = {
    transform: `translateX(${clamp(
      (currentTimeMs / audioDurationMs) * (waveFormContainerRef.current?.offsetWidth || 0),
      0,
      (waveFormContainerRef.current?.offsetWidth ?? 0) - 1
    )}px)`,
  };

  return (
    <div className={cx(containerStyles({ playButtonVariant, disabled }), className)}>
      <div className={tipBoxStyles}>
        {defaultTip && errorTipType === "none" && (
          <div className={tipStyles({ level: "default" })}>{defaultTip}</div>
        )}
        {minErrorTip && (errorTipType === "prevMin" || errorTipType === "currentMin") && (
          <div className={tipStyles({ level: "error" })}>{minErrorTip}</div>
        )}
        {maxErrorTip && (errorTipType === "prevMax" || errorTipType === "currentMax") && (
          <div className={tipStyles({ level: "error" })}>{maxErrorTip}</div>
        )}
      </div>
      <div className={topStyles({ playButtonVariant })}>
        {playButtonVariant === "inline" && (
          <div className={playButtonInlineWrapper}>
            <PlayButton
              isPlaying={isPlaying}
              onTogglePlay={handleTogglePlay}
              isPending={isLoading}
              size={trimMode === "none" ? "sm" : "md"}
              ref={playButtonRef}
            />
          </div>
        )}
        {trimMode !== "none" && (
          <div
            className={waveformContainerStyles}
            role="button"
            tabIndex={0}
            onPointerEnter={handlePointerEnterWaveformContainer}
            onPointerLeave={handlePointerLeaveWaveformContainer}
            ref={waveFormContainerRef}
            style={{ pointerEvents: isLoading ? "none" : "auto" }}
          >
            <div
              className={waveformInnerStyles}
              onPointerDown={(e) => !isLoading && onWaveformPointerdown(e, true)}
            >
              {trimMode === "regions" && !isLoading && (
                <div
                  className={topFloaterTimelineIndicatorOuter}
                  style={topFloaterTimelineIndicatorPosition}
                >
                  <div className={topFloaterTimelineIndicatorStyles}></div>
                  <button
                    className={topFloaterAddButtonStyles}
                    onClick={handleAdd}
                    disabled={!canAddRegion}
                  >
                    <Icon name="add" />
                  </button>
                </div>
              )}
              {isLoading && (
                <div className={loadingStyles}>
                  <div className={css({ position: "absolute", lineHeight: 0 })}>
                    <Spinner size={16} />
                  </div>
                </div>
              )}
              {!isLoading && svg && (
                <div className={waveformStyles} dangerouslySetInnerHTML={{ __html: svg }} />
              )}
              <div className={regionsStyles}>
                {regions.map((region) => (
                  <AudioTrimmerRegion
                    key={region.id}
                    region={region}
                    audioDuration={audioDurationMs}
                    trimMode={trimMode}
                    handleRegionResize={handleRegionResize}
                    handleCommitRegion={handleCommitRegion}
                    onStartMoving={handleStartMoving}
                    onDoneMoving={handleDoneMoving}
                    regionCount={regions.length}
                    handleJumpToProgress={onWaveformPointerdown}
                    onSelect={handleSelectRegion}
                    active={region.id === selectedRegionId}
                    height={WAVEFORM_HEIGHT}
                    disabled={disabled}
                    showDuration={trimMode === "duration"}
                  />
                ))}
              </div>
            </div>
            {trimMode === "regions" && (
              <div className={topFloaterStyles} style={topFloaterPosition}>
                <div className={topFloaterInnerStyles}>
                  <div className={topFloaterTimeStyles}>
                    {formatTimeMs(waveformContainerPointerPosition.timeMs)}
                  </div>
                </div>
              </div>
            )}
            <div
              className={playheadStyles}
              onPointerDown={handlePlayheadDrag}
              style={playheadPosition}
            />
          </div>
        )}
      </div>
      <div className={bottomStyles({ playButtonVariant })}>
        {(playButtonVariant === "bottom" || !hideTimestamps) && (
          <div className={controlsContainerStyles}>
            {playButtonVariant === "bottom" && (
              <PlayButton
                isPlaying={isPlaying}
                onTogglePlay={handleTogglePlay}
                isPending={isLoading}
                size={trimMode === "none" ? "sm" : "md"}
                ref={playButtonRef}
              />
            )}
            {!hideTimestamps && (
              <div
                className={timeStyles}
                onClick={handleClickTimestamp}
                role="button"
                tabIndex={-1}
              >
                <span>{formatTimeMs(currentTimeMs)}</span> /{" "}
                <span>{formatTimeMs(audioDurationMs)}</span>
              </div>
            )}
          </div>
        )}
        {(bottom || trimMode === "regions") && (
          <div className={bottomRightStyles}>
            {trimMode === "regions" && (
              <Button
                variant="secondary"
                icon={<Icon name="delete-bare" />}
                disabled={!canDelete}
                onClick={handleDelete}
              >
                {t("mirage-studio:audio-trimmer.delete-cut")}
              </Button>
            )}
            {bottom}
          </div>
        )}
      </div>
    </div>
  );
}

const containerStyles = cva({
  base: {
    width: "100%",
    height: "auto",
    display: "flex",
    flexWrap: "wrap",
  },
  variants: {
    playButtonVariant: {
      bottom: {
        gap: "1px",
        flexWrap: "wrap",
        alignItems: "center",
      },
      inline: {
        flexWrap: "wrap",
        display: "flex",
        rowGap: "18px",
      },
    },
    disabled: {
      true: {
        pointerEvents: "none",
        opacity: "0.5",
        cursor: "not-allowed",
      },
      false: {},
    },
  },
});

const tipBoxStyles = css({
  width: "100%",
  flex: "0 0 100%",
  textStyle: "body-2-subtle",
});

const tipStyles = cva({
  base: {
    textAlign: "center",
  },
  variants: {
    level: {
      error: {
        color: "$error",
      },
      default: {
        color: "$gray.500",
      },
    },
  },
});

const bottomStyles = cva({
  base: {
    width: "100%",
    flex: "0 0 100%",
    display: "flex",

    "&:empty": {
      display: "none",
    },
  },
  variants: {
    playButtonVariant: {
      bottom: {},
      inline: {
        justifyContent: "flex-end",
      },
    },
  },
});

const bottomRightStyles = css({
  display: "flex",
  justifyContent: "flex-end",
  minWidth: "260px",
  gap: "12px",
});

const topStyles = cva({
  base: {
    width: "100%",
    display: "flex",
  },
  variants: {
    playButtonVariant: {
      bottom: {
        flex: 1,
      },
      inline: {
        flex: "0 0 100%",
      },
    },
  },
});

const playButtonInlineWrapper = css({
  marginRight: "12px",
  flex: 0,
  display: "flex",
  alignItems: "center",
});

const waveformContainerStyles = css({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  overflow: "visible",
  width: "100%",
  height: WAVEFORM_OUTER_HEIGHT,
  flex: 1,
});

const waveformInnerStyles = css({
  height: WAVEFORM_HEIGHT,
  position: "relative",
  backgroundColor: "#1d1e23",
  borderRadius: "8px",
  overflow: "hidden",
});

const waveformStyles = css({
  width: "100%",
  position: "absolute",
  height: "100%",
  "& > svg": {
    opacity: "0.5",
    fill: "$gray.600",
  },
});

const regionsStyles = css({
  display: "block",
  position: "relative",
  width: "100%",
  height: "100%",
});

const controlsContainerStyles = css({
  width: "100%",
  display: "flex",
  gap: "12px",
  alignItems: "center",
});

const timeStyles = css({
  fontVariant: "tabular-nums",
  textStyle: "body-3-subtle",
  "& > span": {
    "&:first-child": {},
    "&:last-child": {
      color: "$gray.400",
    },
  },
});

const playheadStyles = css({
  position: "absolute",
  left: "-11px",
  height: PLAYHEAD_HEIGHT,
  zIndex: "token(zIndex.$content)",
  overflow: "hidden",
  padding: "0 10px",
  "&::before": {
    content: "' '",
    display: "block",
    width: "2px",
    height: PLAYHEAD_HEIGHT,
    backgroundColor: "$gray.100",
    borderRadius: "1px",
  },
});

const loadingStyles = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  height: "100%",
  color: "$gray.400",
});

const topFloaterStyles = css({
  position: "absolute",
  top: "32px",
  left: 0,
  display: "flex",
  width: 0,
  justifyContent: "center",

  overflow: "visible",
  textStyle: "body-4-subtle",
});

const topFloaterInnerStyles = css({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  position: "relative",
  userSelect: "none",
});

const topFloaterTimeStyles = css({
  color: "$sunny",
});

const topFloaterTimelineIndicatorOuter = css({
  position: "absolute",
  bottom: 0,
  top: 0,
  height: WAVEFORM_HEIGHT,
  display: "grid",
  gridTemplateColumns: "1fr",
  gridTemplateRows: "1fr",
  width: `${TOP_FLOATER_INDICATOR_WIDTH}px`,
  zIndex: "$overContent",
});

const topFloaterTimelineIndicatorStyles = css({
  height: WAVEFORM_HEIGHT,
  gridRow: "1",
  gridColumn: "1",
  width: "2px",
  justifySelf: "center",
  alignSelf: "stretch",
  // this is a custom dashed background with round linecaps,
  // note: the stroke color is URL encoded: #FFC333 -> %23FFC333
  backgroundImage: `
    url("data:image/svg+xml;utf8,<svg width='4' height='10' viewBox='0 0 4 10' xmlns='http://www.w3.org/2000/svg'>
    <line opacity='0.5' x1='1' y1='2' x2='1' y2='10' stroke='%23FFC333' stroke-width='2' stroke-linecap='round' stroke-dasharray='5'/>
    </svg>")
  `,
  backgroundRepeat: "repeat-y", // repeat the svg vertically
});

const topFloaterAddButtonStyles = css({
  gridRow: "1",
  gridColumn: "1",
  justifySelf: "center",
  alignSelf: "center",
  position: "relative",
  backgroundColor: "$sunny",
  borderRadius: "50%",
  width: "16px",
  height: "16px",
  color: "black",
  justifyContent: "center",
  alignItems: "center",
  display: "flex",
  transition: "scale 150ms ease",
  scale: 1,
  "& > svg": {
    width: "11px",
    height: "11px",
    opacity: "1",
  },
  "& > svg > path": {
    strokeWidth: "2px",
  },
  "&:disabled": {
    opacity: "0",
    scale: 0.1,
    pointerEvents: "none",
  },
  "&:active": {
    scale: 1.4,
  },
  "&:not(:disabled):hover": {
    boxShadow: "$sunnyGlow",
    "& > svg": {
      scale: 1.3,
    },
  },
});
